/**
 * The chrome every public page wears, and the one place a URL is written down.
 *
 * The production URL contract is not a free choice: `POST /incidents` hands a reporter
 * `/incidents/{slug}` and `POST /risk-reports` hands out `/risk-reports/{id}` the instant they are
 * created, and `GET /sitemap.xml` emits these paths for a crawler. So the nav below and the
 * server's `_STATIC_UI_PATHS` are two halves of one list, and a test on the API side pins the
 * count so a page added here without a line there goes unindexed loudly rather than quietly.
 *
 * Note what is absent: the original design's `/gtm/` prefix. That existed so the review artifact could
 * be opened from a filesystem; in production the tools live at the document root, because the
 * whole GTM thesis is one URL per subject and `/gtm/incidents/…` would put a directory nobody
 * searches for in front of every one of them.
 */
import type { ReactNode } from "react"
import { trackCta } from "./events"

import { CONSOLE } from "./hosts"
import { PORTFOLIO_LINE, others } from "./portfolio"

export const NAV: readonly (readonly [string, string])[] = [
  ["/tools/incidents", "Incidents"],
  ["/tools/risk-profiles", "Risk profiles"],
  ["/tools/categories", "Failure modes"],
  ["/tools/gate", "Admission gate"],
  ["/tools/risk-report", "Risk report"],
  ["/tools/exposure", "Exposure"],
  ["/tools/state-of-agent-risk", "The drop"],
]

export const FOOTER: readonly (readonly [string, string])[] = [
  ["/tools", "Free tools"],
  ["/pricing", "Pricing"],
  ["/partners", "Founding partners"],
  ["/faq", "FAQ"],
  ["/changelog", "Changelog"],
  ["/privacy", "Privacy"],
  ["/terms", "Terms"],
  ["/tools/unsubscribe", "Unsubscribe"],
  ["/contact", "Contact"],
]

/**
 * The links that leave this origin, and the reason they are a separate list.
 *
 * The console and the staff surface are separate builds on separate hosts (see hosts.ts), so a
 * root-relative href to either resolves against agitome.com and 404s. They must be absolute, which
 * is what `scripts/crosslinks.mjs` enforces — and until this list existed the console was DEPLOYED
 * AND UNREACHABLE: live at app.agitome.com with nothing on the public site linking to it, so the
 * only way in was knowing the hostname.
 *
 * The staff surface is deliberately NOT here. It is ours, it is noindex, and advertising its address
 * on a marketing page invites exactly the traffic it has no reason to see.
 */
// The console is at /console on its host now, not at the root of it. Linking the ORIGIN
// would land on a path that redirects at best and 404s at worst.
const OFF_ORIGIN: [string, string][] = [[CONSOLE, "Console"]]

export function Nav({ current }: { current?: string }) {
  return (
    <div className="band--ink" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <nav className="nav">
        <div className="wrap nav__in">
          <a className="wordmark" href="/">agitome</a>
          <div className="nav__links">
            {NAV.map(([href, label]) => (
              <a key={href} href={href} aria-current={href === current ? "page" : undefined}>
                {label}
              </a>
            ))}
          </div>
          <div className="nav__cta">
            {/* The product's primary call to action, and the one that appears in more than one
                place. `placement` is what makes the event worth having: the same words in the
                nav and in a hero are not the same button, and knowing which converts is the
                difference between moving one and guessing about it. */}
            <a
              className="btn btn--primary"
              href="/tools/risk-report"
              onClick={() => trackCta("risk_report", "nav", "/tools/risk-report")}
            >
              Pull a risk report
            </a>
          </div>
        </div>
      </nav>
    </div>
  )
}

export function Footer() {
  return (
    <div className="band--ink" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      <footer className="foot">
        {/* THE REST OF THE PORTFOLIO. Neither site mentioned the other existed, and here it is
            load-bearing rather than decorative: agitome issues no credentials of its own, so a
            visitor persuaded by this page needs to know authoxi exists before they can have an
            account at all. That was discoverable only from the sign-in screen. */}
        {others("agitome").length > 0 ? (
          <div className="wrap foot-portfolio">
            <p className="fine">{PORTFOLIO_LINE}</p>
            {others("agitome").map((product) => (
              <a key={product.id} href={product.href} rel="external">
                <strong>{product.name}</strong> — {product.what}
              </a>
            ))}
          </div>
        ) : null}

        <div className="wrap row row--between">
          <p>agitome — the risk layer for the agentic internet.</p>
          <p className="row" style={{ gap: "var(--s4)" }}>
            {FOOTER.map(([href, label]) => (
              <a key={href} href={href}>{label}</a>
            ))}
            {OFF_ORIGIN.map(([href, label]) => (
              <a key={href} href={href}>{label}</a>
            ))}
          </p>
        </div>
        <div className="wrap row row--between foot__legal">
          <p>© 2026 agitome. All rights reserved.</p>
          <p>Product and company names are the trademarks of their respective owners.</p>
        </div>
      </footer>
    </div>
  )
}

/** A whole public page: nav, content, footer. */
export function Shell({ current, children }: { current?: string; children: ReactNode }) {
  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <Nav current={current} />
      <main id="main">{children}</main>
      <Footer />
    </>
  )
}
